// Property venture maths — buy, force the value up, then flip or rent.
//
// The property lesson pack, Rich-Dad style:
//   • FORCED APPRECIATION — renovation is the one lever the player controls:
//     spend money to make the thing WORTH more (unlike trade, where you only
//     wait for the market).
//   • FLIP vs CASHFLOW — the core choice. Sell for a one-time gain (timed
//     against the market mood), or put a tenant in and collect rent forever.
//   • IDLE PROPERTY BLEEDS — an empty property costs upkeep every single week;
//     a rented one pays you instead. Ownership is never neutral.
//
// Same architecture as trade.ts: the weekly listings are DERIVED from
// (rngSeed, week) — nothing stored, nothing to save-scum — and the sale price
// depends on the market condition of the week you SELL in, which is unknown
// when you buy.

import { nextFloat } from '../rng/prng.js';

export const MAX_PROPERTIES = 2; // hold at most 2 — forces real choices
export const LISTINGS_SIZE = 3; // properties listed each week
export const MAX_RENOVATIONS = 3; // levels per property
export const RENOVATION_LIFT = 0.18; // each level adds +18% to the value
export const RENOVATION_COST_PCT = 0.12; // each level costs 12% of fair value
export const UPKEEP_PCT = 0.015; // weekly upkeep on an EMPTY property (1.5% of value)
export const RENT_PCT = 0.012; // weekly rent on a RENTED property (1.2% of value)

type CatalogEntry = {
  defId: string;
  name: string;
  emoji: string;
  basePrice: number; // honest fair-value anchor
  spread: number; // sale-price volatility — width of the value range
};

const CATALOG: CatalogEntry[] = [
  { defId: 'backyard-room', name: 'Backyard Room', emoji: '🛖', basePrice: 350, spread: 0.26 },
  { defId: 'empty-plot', name: 'Empty Plot', emoji: '🏝️', basePrice: 400, spread: 0.4 },
  { defId: 'container-cafe', name: 'Container Café', emoji: '🛻', basePrice: 450, spread: 0.32 },
  { defId: 'tuckshop-unit', name: 'Tuckshop Unit', emoji: '🏪', basePrice: 520, spread: 0.3 },
  { defId: 'fixer-upper', name: 'Fixer-Upper House', emoji: '🏚️', basePrice: 650, spread: 0.34 },
  { defId: 'studio-flat', name: 'Studio Flat', emoji: '🏢', basePrice: 720, spread: 0.24 },
  { defId: 'corner-shop', name: 'Corner Shop', emoji: '🏬', basePrice: 850, spread: 0.28 },
  { defId: 'old-warehouse', name: 'Old Warehouse', emoji: '🏭', basePrice: 980, spread: 0.36 },
];

/** One listing on this week's property board. */
export type PropertyListing = {
  slot: number; // 0..LISTINGS_SIZE-1
  defId: string;
  name: string;
  emoji: string;
  buyPrice: number; // asking price this week
  fairValue: number; // honest worth — the anchor renovations multiply
  spread: number;
};

const clamp01 = (n: number): number => Math.max(0, Math.min(1, n));

/** Deterministic float for a catalog entry in a week — independent of the shared
 *  rng cursor, so the board is a reproducible property of the week. */
function draw(seed: number, week: number, key: number, k: number): number {
  return nextFloat(seed, week * 104729 + key * 23 + k).value;
}

/** The 3 properties listed this week, derived from (seed, week). */
export function generateListings(seed: number, week: number): PropertyListing[] {
  const ranked = CATALOG.map((c, i) => ({ c, i, r: draw(seed, week, i, 0) }))
    .sort((a, b) => a.r - b.r)
    .slice(0, LISTINGS_SIZE);
  return ranked.map((entry, slot) => {
    const c = entry.c;
    const fairValue = Math.round(c.basePrice * (0.9 + 0.2 * draw(seed, week, entry.i, 1))); // ±10%
    const dealFactor = 0.75 + 0.32 * draw(seed, week, entry.i, 2); // [0.75, 1.07] of fair value
    return {
      slot,
      defId: c.defId,
      name: c.name,
      emoji: c.emoji,
      buyPrice: Math.round(fairValue * dealFactor),
      fairValue,
      spread: c.spread,
    };
  });
}

type Holding = {
  fairValue: number;
  spread: number;
  renovations: number;
};

/** Current honest value = fair value lifted by renovations (forced appreciation). */
export function propertyValue(p: Holding): number {
  return Math.round(p.fairValue * (1 + RENOVATION_LIFT * p.renovations));
}

/** Cost of the NEXT renovation level (a % of the base fair value). */
export function renovationCost(p: Holding): number {
  return Math.max(10, Math.round(p.fairValue * RENOVATION_COST_PCT));
}

/** Sale price = position in the value range set by the week's market condition
 *  (0 = ice-cold buyers' market … 1 = red-hot sellers'). Property doesn't
 *  depreciate like trade goods — the risk is the market, not the clock. */
export function saleValue(p: Holding, condition: number): number {
  const v = propertyValue(p);
  const low = v * (1 - p.spread / 2);
  const high = v * (1 + p.spread / 2);
  return Math.max(0, Math.round(low + (high - low) * clamp01(condition)));
}

/** Weekly upkeep on an EMPTY property (a positive magnitude). */
export function upkeepCost(p: Holding): number {
  return Math.max(2, Math.round(propertyValue(p) * UPKEEP_PCT));
}

/** Weekly rent collected on a RENTED property (tenant covers the upkeep). */
export function rentIncome(p: Holding): number {
  return Math.max(3, Math.round(propertyValue(p) * RENT_PCT));
}
